/**
 * packages/shared/src/dto/asset-components.ts
 *
 * Probe asset grouping (Plan 3): a server's accelerator children with their
 * own FMV, next to the server's System FMV (ADR 0007). The footer is computed
 * from exactly the rows in `components`, so the table and its total can never
 * disagree.
 *
 * lowUsd/highUsd are null on the client endpoint: client_readonly is not
 * granted fmv_low_usd / fmv_high_usd (609, 823; the 578/582 doctrine). Only
 * the ops endpoint fills them.
 */
import { z } from 'zod';

export const fmvSummarySchema = z.object({
  valueUsd: z.number(),
  lowUsd: z.number().nullable(),
  highUsd: z.number().nullable(),
  confidenceScore: z.number().nullable(),
  pricedAt: z.string(),
});
export type FmvSummary = z.infer<typeof fmvSummarySchema>;

export const assetComponentSchema = z.object({
  assetId: z.string().uuid(),
  deviceIndex: z.number().int().nullable(),
  modelName: z.string().nullable(),
  serialMasked: z.string().nullable(),
  skuId: z.string().uuid().nullable(),
  /** SIL-1124: the matched catalog SKU's own model name — distinct from
   * `modelName` above (always the raw probe reading). null when unmatched.
   * UI consumers must display THIS for a matched component, never skuId. */
  skuModelName: z.string().nullable(),
  skuMatchStatus: z.enum(['verified', 'inferred', 'unmatched']),
  fmv: fmvSummarySchema.nullable(),
  lifecycleStateId: z.string(),
  lastSeen: z.string().nullable(),
  isMissing: z.boolean(),
});
export type AssetComponentDto = z.infer<typeof assetComponentSchema>;

/**
 * SIL-1290: the display floor for a server whose chassis SKU is unmatched.
 * When the chassis has its own snapshot, that value is authoritative alone
 * (source: 'snapshot') — children are never added on top of it, since the
 * Components table already shows the part-out value. Only when the chassis
 * has no snapshot at all does the priced-children sum stand in for it
 * (source: 'components_only'), with `partial` set when fewer than all
 * children are priced yet.
 */
export const effectiveSystemFmvSchema = z.object({
  valueUsd: z.number(),
  source: z.enum(['snapshot', 'components_only']),
  partial: z.boolean(),
});
export type EffectiveSystemFmv = z.infer<typeof effectiveSystemFmvSchema>;

export const assetComponentsSchema = z.object({
  parentAssetId: z.string().uuid(),
  systemFmv: fmvSummarySchema.nullable(),
  components: z.array(assetComponentSchema),
  acceleratorTotal: z.number().nullable(),
  pricedCount: z.number().int(),
  totalCount: z.number().int(),
  deltaFromSystem: z.number().nullable(),
  effectiveSystemFmv: effectiveSystemFmvSchema.nullable(),
});
export type AssetComponentsDto = z.infer<typeof assetComponentsSchema>;

const cents = (n: number) => Math.round(n * 100) / 100;

/** "Accelerator total: $X (N of M priced)" and its difference from System FMV. */
export function computeComponentFooter(
  components: readonly Pick<AssetComponentDto, 'fmv'>[],
  systemFmv: FmvSummary | null,
): Pick<AssetComponentsDto, 'acceleratorTotal' | 'pricedCount' | 'totalCount' | 'deltaFromSystem'> {
  const priced = components.flatMap((c) => (c.fmv ? [c.fmv.valueUsd] : []));
  const acceleratorTotal = priced.length === 0 ? null : cents(priced.reduce((sum, v) => sum + v, 0));
  return {
    acceleratorTotal,
    pricedCount: priced.length,
    totalCount: components.length,
    deltaFromSystem: acceleratorTotal === null || systemFmv === null ? null : cents(acceleratorTotal - systemFmv.valueUsd),
  };
}

/**
 * SIL-1290: snapshot value when present (children are never added on top of
 * it — no max(), no addition, per the "chassis only" rule); else the sum of
 * priced children stands in as the display floor, `partial` when it's not
 * all of them; else null (nothing priced at all).
 */
export function computeEffectiveSystemFmv(
  systemFmv: FmvSummary | null,
  footer: Pick<AssetComponentsDto, 'acceleratorTotal' | 'pricedCount' | 'totalCount'>,
): EffectiveSystemFmv | null {
  if (systemFmv !== null) return { valueUsd: systemFmv.valueUsd, source: 'snapshot', partial: false };
  if (footer.acceleratorTotal === null) return null;
  return { valueUsd: footer.acceleratorTotal, source: 'components_only', partial: footer.pricedCount < footer.totalCount };
}

export function buildAssetComponents(
  parentAssetId: string,
  systemFmv: FmvSummary | null,
  components: AssetComponentDto[],
): AssetComponentsDto {
  const footer = computeComponentFooter(components, systemFmv);
  return {
    parentAssetId,
    systemFmv,
    components,
    ...footer,
    effectiveSystemFmv: computeEffectiveSystemFmv(systemFmv, footer),
  };
}
